import * as React from "react";
import Box from "@mui/material/Box";
import {
  Backdrop,
  CircularProgress,
  Collapse,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CorporateCategoryCardList from "./CardCorporateCategory";
import BreadCrumbs from "../../../components/navbar/BreadCrumb";
import SearchBar from "../../../components/ui/SearchBar";
import LoaderImg from "../../../components/ui/loader";
import { TransitionGroup } from "react-transition-group";
import axiosErrorHandler from "../../../ErrorHandler/axiosErrHandler";
import { allCorporateList } from "../../../APIConfig/NewCorporateApiConfig";


// corporate list for shwoing all corporate scenario list
export default function NewCategoryPageCorporate({ variant }) {

  const [data, setData] = React.useState([]);
  const [memInput, setMemInput] = React.useState("");
  const [mem, setMem] = React.useState([]);
  const [loader, setLoader] = React.useState(false);
  const [selectedCategory, setSelectedCategory] = React.useState("all");

  // Dynamically extract unique categories from list
  const categoryOptions = React.useMemo(() => {
    if (!Array.isArray(mem)) return [];
    const set = new Set();
    mem.forEach((item) => {
      if (item?.category_name) {
        set.add(item.category_name);
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [mem]);

  // Separate modular filter function handling category and search
  const applyFilter = (category, search, list = mem) => {
    let result = Array.isArray(list) ? list : [];

    if (category && category !== "all") {
      result = result.filter((item) => item?.category_name === category);
    }

    const query = (search || "").trim().toLowerCase();
    if (query) {
      result = result.filter((item) =>
        item?.name?.toLowerCase().includes(query)
      );
    }

    return result;
  };

  // ---- API / MOCK HANDLER ----
  const getApi = async () => {
    setLoader(true);
    try {
      const response = await allCorporateList();
      const list = response?.data || [];
      setMem(list);
      setData(applyFilter(selectedCategory, memInput, list));
    } catch (error) {
      setData([]);
      setMem([]);
      axiosErrorHandler(error);
    } finally {
      setLoader(false);
    }
  };

  // ---- SEARCH HANDLER ----
  const handleSearch = (e) => {
    const value = e.target.value;
    setMemInput(value);
    setData(applyFilter(selectedCategory, value, mem));
  };

  // ---- CATEGORY HANDLER ----
  const handleCategoryChange = (e) => {
    const value = e.target.value;
    setSelectedCategory(value);
    setData(applyFilter(value, memInput, mem));
  };

  React.useEffect(() => {
    getApi();
  }, []);

  const breadcrumbs = [
    { name: "Dashboard", link: "/" },
    { name: "All Scenarios",  },
  ];

  return (
    <Stack>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loader}
      >
        <LoaderImg />
        <CircularProgress color="inherit" />
      </Backdrop>

      <BreadCrumbs breadcrumbs={breadcrumbs} />

      <Box style={{ width: "100%", padding: 40 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Typography variant="h2">All Scenarios</Typography>

          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              select
              value={selectedCategory}
              onChange={handleCategoryChange}
              sx={{ minWidth: 200, borderRadius: 2 }}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      bgcolor: "#16181F",
                      color: "#EAEAEB",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      maxHeight: 340,
                      "& .MuiMenuItem-root": {
                        fontSize: "14px",
                        bgcolor: "transparent",
                        "&:hover": {
                          bgcolor: "rgba(0, 255, 255, 0.08)",
                          color: "#00FFFF",
                        },
                        "&.Mui-selected": {
                          bgcolor: "rgba(0, 255, 255, 0.15) !important",
                          color: "#00FFFF",
                        },
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value="all">All Categories</MenuItem>
              {categoryOptions.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </TextField>

            <SearchBar
              value={memInput}
              placeholder="Search"
              onChange={handleSearch}
            />
          </Stack>
        </Stack>

        <TransitionGroup style={{ marginTop: 20 }}>
          {data?.length > 0 &&
            data.map((item, index) => (
              <Collapse in={true} key={`${index}-${item?.id}`}>
                <CorporateCategoryCardList
                  item={item}
                  variant={variant}
                  loader={loader}
                  setLoader={setLoader}
                />
              </Collapse>
            ))}
        </TransitionGroup>

        {data?.length === 0 && (
          <Typography
            variant="h3"
            sx={{ textAlign: "center", mt: "50px" }}
          >
            No result found
          </Typography>
        )}
      </Box>
    </Stack>
  );
}